import { valibotResolver } from "@hookform/resolvers/valibot";
import { PlusCircle } from "@phosphor-icons/react";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";

import MaxLengthInput from "common/components/MaxLengthInput";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { getSchemaMaxLength } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import useWorkspaceNavigate from "common/hooks/useWorkspaceNavigate";

import useLeadListMutations from "../../datahooks/useLeadListMutations";
import { CreateListSchema, type CreateListSchemaType } from "../../schemas";
import AddSourceFlow from "../AddSourceFlow";

export default function LeadListCreate() {
  const isTabletOrDesktop = useTwBreakpoint("lg");
  const { relativeNavigate } = useWorkspaceNavigate();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [listId, setListId] = useState<string>();

  const { createList, isCreatingList } = useLeadListMutations();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CreateListSchemaType>({
    defaultValues: { name: "" },
    resolver: valibotResolver(CreateListSchema),
  });
  const shouldCreateSourceRef = useRef(false);

  const Component = isTabletOrDesktop ? Dialog : Drawer;
  const Content = isTabletOrDesktop ? DialogContent : DrawerContent;
  const Header = isTabletOrDesktop ? DialogHeader : DrawerHeader;
  const Body = isTabletOrDesktop ? DialogBody : DrawerBody;
  const Footer = isTabletOrDesktop ? DialogFooter : DrawerFooter;

  function goToList(listId: string) {
    relativeNavigate(`lists/${listId}`);
  }
  function resetState() {
    shouldCreateSourceRef.current = false;
    reset();
    setListId(undefined);
  }
  function onSubmit(data: CreateListSchemaType) {
    createList(data).then(({ id }) => {
      if (shouldCreateSourceRef.current) {
        setListId(id);
      } else {
        goToList(id);
      }
    });
  }

  return (
    <>
      <Button
        leftIcon={<PlusCircle weight="fill" />}
        size={isTabletOrDesktop ? "sm" : "md"}
        onClick={() => setIsDialogOpen(true)}
      >
        Create list
      </Button>

      <Component
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onAfterClose={resetState}
      >
        <Content size={listId ? "lg" : "md"}>
          {listId ? (
            <AddSourceFlow listId={listId} />
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Header title="Create new list" />

              <Body>
                <FormField
                  label="List name"
                  htmlFor="listName"
                  error={errors.name?.message}
                >
                  <MaxLengthInput
                    id="listName"
                    placeholder="Enter a list name here"
                    characterLength={watch("name")?.length}
                    maxLength={getSchemaMaxLength(
                      CreateListSchema.entries.name,
                    )}
                    autoFocus
                    {...register("name")}
                  />
                </FormField>
              </Body>

              <Footer className="justify-end">
                <Button
                  type="submit"
                  variant="secondary-black"
                  isLoading={!shouldCreateSourceRef.current && isCreatingList}
                  disabled={shouldCreateSourceRef.current && isCreatingList}
                >
                  Create list
                </Button>
                <Button
                  type="submit"
                  onClick={() => {
                    shouldCreateSourceRef.current = true;
                  }}
                  isLoading={shouldCreateSourceRef.current && isCreatingList}
                  disabled={!shouldCreateSourceRef.current && isCreatingList}
                >
                  Add leads
                </Button>
              </Footer>
            </form>
          )}
        </Content>
      </Component>
    </>
  );
}
